/**
 * Единственный источник правды по составу навигации.
 *
 * Пунктов два представления: горизонтальный NavMenu на публичных страницах и
 * выпадающий список под иконкой пользователя в консоли. Раньше это были два
 * независимых набора ссылок, и половина экранов не встречалась ни в одном.
 *
 * Все подписи живут в namespace nav_menu. Пункты модели доступа стенда
 * (например, управление подписками) добавляет её модуль — см. access/AccessModule.ts.
 */

export type NavAccess = 'public' | 'admin';

export interface NavLinkDef {
    to: string;
    /** Ключ в public/locales/<lng>/nav_menu.json. */
    labelKey: string;
    access: NavAccess;
}

export interface NavSection {
    id: string;
    /** Задан — в навбаре это выпадающая группа, в консоли — заголовок блока. */
    labelKey?: string;
    links: NavLinkDef[];
}

export interface NavVisibility {
    isAdmin: boolean;
}

export const NAV_SECTIONS: readonly NavSection[] = [
    {
        id: 'main',
        links: [
            // Для гостя это лендинг, для залогиненного — консоль: корень
            // разводится по авторизации в App.tsx.
            { to: '/', labelKey: 'home_page', access: 'public' }
        ]
    },
    {
        // Все три дистрибутива живут на одной странице, выпадающий список
        // из трёх пунктов ей больше не нужен.
        id: 'downloads',
        links: [
            { to: '/downloads', labelKey: 'downloads', access: 'public' }
        ]
    },
    {
        id: 'docs',
        links: [
            { to: '/wiki/quickStart', labelKey: 'documentation', access: 'public' }
        ]
    },
    {
        id: 'admin',
        labelKey: 'administration',
        links: [
            { to: '/firmware', labelKey: 'firmware', access: 'admin' },
            { to: '/settings', labelKey: 'settings', access: 'admin' },
            { to: '/statistics', labelKey: 'statistics', access: 'admin' },
            { to: '/onlineDrones', labelKey: 'online_drones', access: 'admin' },
            { to: '/users', labelKey: 'users', access: 'admin' }
        ]
    }
];

function isVisible(link: NavLinkDef, { isAdmin }: NavVisibility): boolean {
    switch (link.access) {
        case 'admin':
            return isAdmin;
        case 'public':
            return true;
    }
}

/**
 * Секции без доступных пунктов выкидываются целиком.
 * adminLinks — пункты администрирования от модуля модели доступа.
 */
export function visibleSections(visibility: NavVisibility, adminLinks: NavLinkDef[]): NavSection[] {
    return NAV_SECTIONS
        .map((section) => section.id === 'admin' ? { ...section, links: [...section.links, ...adminLinks] } : section)
        .map((section) => ({ ...section, links: section.links.filter((l) => isVisible(l, visibility)) }))
        .filter((section) => section.links.length > 0);
}
